// src/containers/Receptions/ReceptionList/hooks/useReceptionList.tsx
import { ArrowRightAlt } from "@mui/icons-material";
import { Autocomplete, Chip, TextField, Tooltip } from "@mui/material";
import dateFormat from "dateformat";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useSnackbar } from "$utils/useSnackbar";
import ArrowDownSLineIcon from "remixicon-react/ArrowDownSLineIcon";
import ArrowUpSLineIcon from "remixicon-react/ArrowUpSLineIcon";
import { useWMSConfigQuery } from "$hooks/useWMSConfigQuery";
import useAuth from "$hooks/useAuth";
import useCompany from "$hooks/useCompany";
import useIsProductDeletable from "$hooks/useIsProductDeletable";
import useJStyles from "$hooks/useJStyles";
import { useListAdapter } from "$hooks/useList/useListAdapter";
import useOrder from "$hooks/useOrder";
import useReception, { STATUS_RECEPTION } from "$hooks/useReception";
import {
  snackbarErrorOptions,
  snackbarSuccessOptions,
  snackbarWarningOptions,
} from "$utils/SnackbarOptions";
import {
  RECEPTION_STATUS_LABELS,
  REFERENCING_STATUS_LABELS,
} from "$utils/typeConstants";

import {
  newEmptyReception,
  RECEPTION_SYNCHRONIZED_STATUS_FILTER,
} from "../types";
import useReceptionActions from "./useReceptionActions";

import { Text } from "$components";
import BataillogDatePicker from "$components/BataillogDatePicker/BataillogDatePicker";
import JBadge from "$components/JBadge/JBadge";
import WarehouseSelector from "$components/JSelectors/WarehouseSelector/WarehouseSelector";
import JTable from "$components/JTable/JTable";
import { useCommonStyles } from "$containers/CommonStyles/CommonStyles";
import OrderSelect from "$containers/Orders/forms/OrderSelect";
import CompanySearchInput from "$forms/CompanySearchInput";
import GenericSelect from "$forms/GenericSelect";
import WarehouseSearchInput from "$forms/WarehouseSearchInput";
import WarehouseSelect from "$forms/WarehouseSelect";
import useTabs from "$hooks/useTabs";
import { appState } from "$reducers";
import {
  CompanyServices,
  EntrepotService,
  EDBService,
  ProductService,
} from "$services";
import { CompanyType, Reception } from "$types";
import ReceptionStatus from "$types/ReceptionStatus";
import ReferencingStatus from "$types/ReferencingStatus";
import { CacheInvalidationAction } from "$utils/cache";
import { tabs } from "$utils/constants";
import { queryClient } from "$utils/queryClient";
import { theme } from "$utils/theme/bataillog";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import useReceptionContext from "./useReceptionContext";
import useReceptionFilters from "./useReceptionFilters";
import ProviderSelect from "../components/ProviderSelect";
import { deleteProduct } from "$api/productApi";

// Stable default: `data = []` creates a new array every render while the query
// is pending, which re-creates getReceptionSynchronizedStatus and re-fires the
// `setReceptions` effect on every render.
const EMPTY_WMS_CONFIGS: any[] = [];

export interface IReceptionsProps {
  className?: string;
}

function useReceptionList(): any {
  const { isAdmin, token, isContributor, user } = useAuth();
  const { filter: companySidebarFilter } = useSelector(appState);
  const { isDeletable: isProductDeletable } = useIsProductDeletable();
  const { data: wmsConfigs = EMPTY_WMS_CONFIGS } = useWMSConfigQuery();
  const { isClient: isClientCompany } = useCompany();
  const { jclasses } = useJStyles();
  const { getSuborderTypeInfo } = useOrder();
  const [openWarningSnackbar] = useSnackbar(snackbarWarningOptions);
  const [openErrorSnackbar] = useSnackbar(snackbarErrorOptions);
  const [openSuccessSnackbar] = useSnackbar(snackbarSuccessOptions);
  const { classes, togglePopin: toggleReceptionPopin, open } = useReception();
  const commonClasses = useCommonStyles();
  const { currentTab } = useTabs();

  // Get shared context
  const { defaultOptions, getServiceType, getEndpoint } = useReceptionContext();

  // State variables
  const [isSaving, setIsSaving] = useState(false);
  const [isWarehouseSelected, setIsWarehouseSelected] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState<boolean>(false);
  const [orderNewValue, setOrderNewValue] = useState(undefined);
  const [receptions, setReceptions] = useState<any>([]);
  const [editedReception, setEditedReception] = useState<any>();
  const [editedRowsId, setEditedRowsId] = useState<any>([0]);
  // CLIENT OWN WAREHOUSES AND LOGICTICIEN LINKED
  const [clientWarehouses, setClientWarehouses] = useState([]);
  const [isLoadingContributorCompanies, setIsLoadingContributorCompanies] =
    useState(false);

  const {
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const companyId = isContributor()
    ? user.userInfo.userCompany._id
    : companySidebarFilter?._id;

  const {
    data: myContributorWarehouses,
    isLoading: isLoadingMyContributorWarehouses,
  } = useQuery({
    queryKey: [
      CacheInvalidationAction.GET_ENTREPOTS,
      isContributor(),
      { company: companyId },
    ],

    queryFn: () => {
      // We use this only if we have the contributor role
      if (isContributor() && !isAdmin()) {
        return EntrepotService.getEntrepots({
          parentCompany: companyId,
          rowsPerPage: 100000,
        });
      }
      return [];
    },

    gcTime: 5 * 1000 * 60,
    enabled: isContributor() && !isAdmin(),
  });

  // Query Contributor Companies
  const fetchContributorCompanies = useCallback(async () => {
    const contributorCompanyIds = [
      ...(user?.userInfo?.userCompany?.contributorOf || []),
      companySidebarFilter?._id,
    ].filter(Boolean); // Remove any undefined/null values

    if (contributorCompanyIds.length === 0) {
      setClientWarehouses([]);
      setIsLoadingContributorCompanies(false);
      return;
    }

    const filtersCC = {
      $inId: contributorCompanyIds,
    };
    const queryKeysCC = [
      CacheInvalidationAction.GET_COMPANIES,
      "contributorCompanies",
      filtersCC,
    ];
    setIsLoadingContributorCompanies(true);
    let companies: any = await queryClient.getQueryData(queryKeysCC);
    if (!companies) {
      companies = await queryClient.fetchQuery({
        queryKey: queryKeysCC,
        queryFn: () => CompanyServices.getCompanies(filtersCC),
        staleTime: 60 * 1000,
      });
      companies = companies.data;
      await queryClient.setQueryData(queryKeysCC, companies);
    }

    const filters = {
      page: 1,
      rowsPerPage: 50000,
      ignoreContributorOf: true,
    };
    let companiesTest;
    companiesTest = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANIES,
      filters,
    ]);
    if (!companiesTest) {
      companiesTest = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_COMPANIES, filters],
        queryFn: () => CompanyServices.getCompanies(filters),
        staleTime: 60 * 1000,
      });
      companiesTest = companiesTest.data;
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        companiesTest
      );
    }
    // console.log("companiesTest", companiesTest);
    // Fetch warehouses for each contributor company
    const warehousesCompanyClientArray = [];
    const clientOwnWarehouses = [];
    const clientLogisticianWarehouses = [];
    await Promise.all(
      companies.map(async (companyOfContributor) => {
        const queryKeysCCWarehouses = [
          CacheInvalidationAction.GET_ENTREPOTS,
          "warehousesCompanyClient",
          { company: companyOfContributor._id },
        ];
        let warehousesCompanyClient: any = await queryClient.getQueryData(
          queryKeysCCWarehouses
        );
        if (!warehousesCompanyClient) {
          warehousesCompanyClient = await queryClient.fetchQuery({
            queryKey: queryKeysCCWarehouses,

            queryFn: () =>
              EntrepotService.getEntrepots({
                company: companyOfContributor._id,
              }),

            staleTime: 60 * 1000,
          });
          await queryClient.setQueryData(
            queryKeysCCWarehouses,
            warehousesCompanyClient
          );
        }

        warehousesCompanyClientArray.push(...warehousesCompanyClient.data);
        clientOwnWarehouses.push(
          ...(warehousesCompanyClient.data || []).filter(
            (e) => e.parentCompany === companyOfContributor._id
          )
        );

        clientLogisticianWarehouses.push(
          ...(warehousesCompanyClient.data || []).filter(
            (e) =>
              e.companies.some(
                (company) => companyOfContributor._id === company._id
              ) &&
              companiesTest
                ?.find((c) => c._id === e.parentCompany)
                ?.companyTypes?.includes(CompanyType.LOGISTICIAN)
          )
        );
      })
    );
    // console.log("warehousesCompanyClientArray", warehousesCompanyClientArray);

    // Get unique warehouses by _id
    const uniqueClientOwnWarehouses = clientOwnWarehouses.filter(
      (warehouse, index, self) =>
        index === self.findIndex((w) => w._id === warehouse._id)
    );
    const uniqueClientLogisticianWarehouses =
      clientLogisticianWarehouses.filter(
        (warehouse, index, self) =>
          index === self.findIndex((w) => w._id === warehouse._id)
      );

    setClientWarehouses([
      ...uniqueClientOwnWarehouses,
      ...uniqueClientLogisticianWarehouses,
    ]);
    setIsLoadingContributorCompanies(false);
  }, [user?.userInfo?.userCompany?.contributorOf, companySidebarFilter?._id]);

  useEffect(() => {
    if (isContributor() && !isAdmin()) {
      fetchContributorCompanies();
    }
  }, [fetchContributorCompanies, isContributor, isAdmin]);

  const finalDefaultFilters = useMemo(() => {
    if (isContributor() && !isAdmin()) {
      const warehouseIds = myContributorWarehouses?.data?.map(
        ({ _id }) => _id
      ) ?? ["impossibleId"];
      const warehouseIdsWithClientWarehouses = [
        ...new Set([...warehouseIds, ...clientWarehouses.map((w) => w._id)]),
      ];
      return {
        ...defaultOptions.defaultFilters,
        warehouse: { $in: warehouseIdsWithClientWarehouses },
      };
    }

    return defaultOptions.defaultFilters;
  }, [
    isContributor,
    isAdmin,
    myContributorWarehouses?.data,
    clientWarehouses,
    defaultOptions.defaultFilters,
  ]);

  // Use the list hook to fetch receptions directly from the gateway endpoint
  const {
    items: itemsArray,
    isFetching,
    filters,
    setFilter,
    pageIndex,
    pageCount,
    removeListItem,
    getParamsAsStrapiString,
    setPageIndex,
    itemsPerPage: pageSize,
    setItemsPerPage: setPageSize,
    fetchItems,
  } = useListAdapter<Reception & { id: number }>(
    getEndpoint(),
    {
      ...defaultOptions,
      defaultFilters: finalDefaultFilters || {},
      defaultSort: defaultOptions.defaultSort,
      defaultPageSize: 20,
      additionalParams: {},
      ignoreSideBarFilter: defaultOptions.ignoreSideBarFilter,
      cachePrefix: defaultOptions.cachePrefix,
      jwt: token,
      enabled:
        !isLoadingMyContributorWarehouses && !isLoadingContributorCompanies,
      cacheKey: CacheInvalidationAction.GET_RECEPTIONS,
    },
    getServiceType()
  );

  // Handle both old Strapi structure (data.data) and new Prisma structure (data directly)
  const items = useMemo(
    () => (itemsArray as any)?.data || itemsArray || [],
    [itemsArray]
  );

  const onAddReception = useCallback(() => {
    setEditedReception({
      ...newEmptyReception,
      id: undefined,
      isTransfer: currentTab === tabs.TRANSFER_TAB,
    });
    toggleReceptionPopin(true);
  }, [currentTab, toggleReceptionPopin]);

  // Use the filters hook
  const {
    dateStart_tfilter: filterDateStart,
    dateEnd_tfilter: filterDateEnd,
    setDateStart_tfilter,
    setDateEnd_tfilter,
    onDateChange,
    onDatePickerClose,
    synchronization_tfilter,
    setSynchronization_tfilter,
    exportReceptions,
    isExporting,
    selectedRowsReception,
    setSelectedRowsReception,
  } = useReceptionFilters(filters, setFilter);

  const [companies, setCompanies] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Query Companies
  const fecthCompanies = useCallback(async () => {
    const filters = {
      isActive: true,
      page: 1,
      rowsPerPage: 1000,
    };
    let companies;
    companies = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANIES,
      filters,
    ]);
    if (!companies) {
      companies = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_COMPANIES, filters],
        queryFn: () => CompanyServices.getCompanies(filters),
        gcTime: 60 * 1000,
      });
      // Handle both old Strapi structure (data.data) and new Prisma structure (data directly)
      companies = companies?.data || companies || [];
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        companies
      );
    }
    setCompanies(companies);
  }, [setCompanies]);

  useEffect(() => {
    fecthCompanies();
  }, [fecthCompanies]);

  // get warehouses
  const fecthWarehouses = useCallback(async () => {
    if (receptions.length > 0) {
      const filters = {
        page: 1,
        rowsPerPage: 1000,
      };

      const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
      let getWarehouses: any = await queryClient.getQueryData(queryKey);
      if (!getWarehouses) {
        getWarehouses = await queryClient.fetchQuery({
          queryKey,
          queryFn: () => EntrepotService.getEntrepots(filters),
          gcTime: 5 * 60 * 1000,
        });
        queryClient.setQueryData(queryKey, getWarehouses);
      }
      // Handle both old Strapi structure (data.data) and new Prisma structure (data directly)
      setWarehouses(getWarehouses?.data || getWarehouses || []);
    }
  }, [receptions]);

  useEffect(() => {
    fecthWarehouses();
  }, [fecthWarehouses]);

  // const getCompaniesFromItems = useMemo(() => {
  //   return (itemsArray as any)?.companies || [];
  // }, [itemsArray]);

  // const getWarehousesFromItems = useMemo(() => {
  //   return (itemsArray as any)?.warehouses || [];
  // }, [itemsArray]);

  const getDockyardsFromItems = useMemo(() => {
    return (itemsArray as any)?.dockyards || [];
  }, [itemsArray]);

  const getProjectsFromItems = useMemo(() => {
    return (itemsArray as any)?.projects || [];
  }, [itemsArray]);

  const getEdbData = useMemo(() => {
    return (itemsArray as any)?.edbData || null;
  }, [itemsArray]);

  const getReceptionCompany = useCallback(
    (reception: any) => {
      const receptionCompanies = companies;
      if (receptionCompanies.length > 0) {
        const company = receptionCompanies.find(
          (c: any) => c._id === reception?.company
        );
        return company;
      }
      return undefined;
    },
    [companies]
  );

  const companiesOptions = useMemo(() => {
    const receptionCompanies = companies;
    if (receptionCompanies.length > 0) {
      return receptionCompanies.filter(
        (c: any) => c.isActive && isClientCompany(c.companyTypes)
      );
    }
    return [];
  }, [isClientCompany, companies]);

  useEffect(() => {
    if (currentTab === 1) {
      setFilter("isTransfer", true);
    } else {
      setFilter("isTransfer", false);
    }
    setFilter("global", undefined);
  }, [currentTab, setFilter]);

  // Extract the actions into a separate hook
  const {
    removeReceptionItem,
    getReceptionSynchronizedStatus,
    updateReception,
    handleClick,
    onSubmitOrder,
    onSubmitReception,
    handleSaveReception,
    getCompaniesOrParent,
    onOrderSelectValueChanged,
    getReceptionSynchronizedStatusButton,
  } = useReceptionActions({
    wmsConfigs,
    token,
    setReceptions,
    fetchItems,
    setEditedReception,
    toggleReceptionPopin,
    setShowOrderModal,
    setOrderNewValue,
    setIsSaving,
    setIsWarehouseSelected,
    editedRowsId,
    setEditedRowsId,
    currentTab,
    items,
  });

  const editedReceptionRefIds = useMemo(() => {
    return (
      editedReception?.receptionReferences
        ?.map((r: any) => r?.id)
        .filter(Boolean) || []
    );
  }, [editedReception]);

  const { data: usedInEdbForEditedReception } = useQuery({
    queryKey: [
      CacheInvalidationAction.IS_RECEPTION_IN_EDB,
      editedReceptionRefIds,
    ],
    queryFn: () => EDBService.isEdbExistForProduct(editedReceptionRefIds),
    enabled: editedReceptionRefIds.length > 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const canDeleteEditedReception = useMemo(() => {
    if (!editedReception?.id) return false;
    if (!editedReceptionRefIds.length) return true;
    return !usedInEdbForEditedReception;
  }, [
    editedReception?.id,
    editedReceptionRefIds.length,
    usedInEdbForEditedReception,
  ]);

  const onDeleteEditedReception = useCallback(async () => {
    if (!editedReception?.id) return;
    if (!canDeleteEditedReception) return;

    try {
      const res = await removeReceptionItem(String(editedReception.id));
      if (res instanceof Error) {
        openErrorSnackbar(
          `Une erreur s'est produite lors de la suppression de la réception ${editedReception.reference}.`
        );
        return;
      }

      const deletionPromises = (editedReception.receptionReferences || []).map(
        async (receptionRef: any) => {
          if (
            (await isProductDeletable(
              receptionRef.productReference,
              editedReception?.company,
              editedReception?.id
            )) &&
            receptionRef.productReference !==
              receptionRef.originalProductReference
          ) {
            try {
              const product = await ProductService.getProductByReference({
                reference: receptionRef.productReference,
                company: editedReception?.company,
              });
              if (product.length === 1) {
                await deleteProduct(token, product[0]._id);
                queryClient.invalidateQueries({
                  queryKey: [CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE],
                });
              }
            } catch {
              openWarningSnackbar(
                `Une erreur s'est produite lors de la suppression du produit ${receptionRef.productReference}.`
              );
            }
          }
        }
      );

      await Promise.all(deletionPromises);
      openSuccessSnackbar(`Réception ${editedReception.reference} supprimée !`);
      await fetchItems();
      toggleReceptionPopin(false);
    } catch {
      openErrorSnackbar(
        `Une erreur s'est produite lors de la suppression de la réception ${editedReception?.reference}.`
      );
    }
  }, [
    canDeleteEditedReception,
    editedReception,
    fetchItems,
    isProductDeletable,
    openErrorSnackbar,
    openSuccessSnackbar,
    openWarningSnackbar,
    removeReceptionItem,
    token,
    toggleReceptionPopin,
  ]);

  const OrderSelectMemo = useCallback(
    ({ currentReception }) => {
      const orders = currentReception?.orders || [];

      const selectedOrderReferences = (orders || [])
        .map((order: any) => order.reference)
        .filter(Boolean);

      return (
        <OrderSelect
          companies={companiesOptions}
          displayCreateOrderOption={isAdmin()}
          onChange={onOrderSelectValueChanged(currentReception)}
          value={orders?.map((order: any) => order.reference)}
          isTransfer={currentTab === tabs.TRANSFER_TAB}
          invalidateQuery
          excludeOrderReferences={selectedOrderReferences}
        />
      );
    },
    [companiesOptions, currentTab, isAdmin, onOrderSelectValueChanged]
  );

  // Update receptions state when items change
  useEffect(() => {
    setReceptions((prev) => {
      let newReceptions: any[] = [];
      if (items) {
        items.forEach((item: any) => {
          newReceptions.push({ ...item });
        });

        newReceptions = synchronization_tfilter
          ? newReceptions.filter(
              (reception: any) =>
                getReceptionSynchronizedStatus(reception) ===
                synchronization_tfilter
            )
          : newReceptions;
      }
      // Bail out when both are empty to avoid a render loop while queries load
      if (prev.length === 0 && newReceptions.length === 0) {
        return prev;
      }
      return newReceptions;
    });
  }, [getReceptionSynchronizedStatus, isAdmin, items, synchronization_tfilter]);

  // If receptions are updated, be sure to update the edited one too.
  // Depend on id only — depending on editedReception itself + setEditedReception
  // caused a Maximum update depth loop (new object every time).
  const editedReceptionId = editedReception?.id;
  useEffect(() => {
    if (!editedReceptionId) return;
    const newReception = receptions.find((r) => r.id === editedReceptionId);
    if (newReception) {
      setEditedReception(newReception);
    }
  }, [receptions, editedReceptionId]);

  const columns = useMemo(() => {
    return [
      {
        Header: "Id",
        filterName: "reference",
        className: "table-start",
        minWidth: 90,
        maxWidth: 90,
        Cell: ({ row: { original: currentReception } }: any) => {
          const company = getReceptionCompany(currentReception);
          return (
            <div>
              {currentReception?.reference && (
                <div
                  onClick={() => handleClick(currentReception)}
                  role="button"
                  tabIndex={0}
                  aria-hidden="true"
                  className={jclasses.tableClickableDiv}
                >
                  <Text type={company?.isActive ? "text" : "disabled"}>
                    {currentReception?.reference || ""}
                  </Text>
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: "Date de réception",
        filterName: "receptionDate",
        filterComponent: () => (
          <BataillogDatePicker
            selectsRange
            startDate={filterDateStart}
            endDate={filterDateEnd}
            onDateChange={onDateChange}
            onCalendarClose={onDatePickerClose}
          />
        ),
        minWidth: 135,
        maxWidth: 135,
        Cell: ({ row: { original: currentReception } }: any) => {
          const company = getReceptionCompany(currentReception);
          return currentReception.id ? (
            <div
              onClick={() => handleClick(currentReception)}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text type={company?.isActive ? "text" : "disabled"}>
                {dateFormat(
                  currentReception.receptionDate,
                  DEFAULT_DATE_FORMAT
                )}
              </Text>
            </div>
          ) : (
            <BataillogDatePicker
              selectedDate={
                currentReception.receptionDate
                  ? new Date(currentReception.receptionDate)
                  : undefined
              }
              onDateChange={(dates) => {
                updateReception(currentReception, "receptionDate", dates);
              }}
            />
          );
        },
      },
      {
        Header: "N° de commande(s)",
        filterName: "orderReference",
        minWidth: 150,
        maxWidth: 150,
        Cell: ({ row: { original: currentReception } }: any) => {
          const company = getReceptionCompany(currentReception);
          return (currentReception?.id &&
            !editedRowsId.includes(currentReception?.id)) ||
            (editedRowsId.includes(currentReception?.id) &&
              currentReception.status !== ReceptionStatus.TODO) ? (
            <div
              style={{ display: "flex", flexFlow: "wrap" }}
              onClick={() => handleClick(currentReception)}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              {currentReception?.orders?.map((order) => (
                <span
                  key={order.id}
                  style={{
                    border: "solid 1px lightgrey",
                    borderRadius: 5,
                    margin: 2,
                    padding: 2,
                    color: company?.isActive ? "initial" : theme.color.gray3,
                  }}
                >
                  {order.reference}
                </span>
              ))}
            </div>
          ) : (
            <OrderSelectMemo currentReception={currentReception} />
          );
        },
      },
      {
        Header: "Entreprise",
        // UI-only filter key (string) to avoid storing `{ $in: [...] }` objects as the input value
        filterName: "__company",
        filterComponent: (props: any) => (
          <CompanySearchInput
            value={props.value || companySidebarFilter?.name}
            disabled={!!companySidebarFilter?.name}
            label=""
            onChange={(val, ids) => {
              props.onChange(val);
              if (companySidebarFilter?._id) {
                setFilter("company", { $eq: companySidebarFilter._id });
              } else if (!!val && ids?.length) {
                setFilter("company", { $in: ids });
              } else {
                setFilter("company", undefined);
              }
            }}
            orderModule
          />
        ),
        maxWidth: 145,
        minWidth: 145,
        Cell: ({ row: { original: currentReception } }: any) => {
          const company = getReceptionCompany(currentReception);
          const companyData = currentReception.company;

          return currentReception.id ? (
            <div
              onClick={() => handleClick(currentReception)}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text type={company?.isActive ? "text" : "disabled"}>
                {companies.find((c: any) => c._id === companyData)?.name}
              </Text>
            </div>
          ) : (
            <Autocomplete
              disabled
              disableClearable
              className={commonClasses.select}
              options={companiesOptions || []}
              value={
                companies?.find(
                  (c: any) => c._id === currentReception.company
                ) ?? null
              }
              getOptionLabel={(option: any) => option?.name || option}
              onChange={(e, data) => {
                updateReception(currentReception, "company", data?._id);
              }}
              renderInput={(params: any) => (
                <TextField {...params} variant="outlined" />
              )}
              ListboxProps={{ style: { width: "fit-content" } }}
              noOptionsText="Pas de résultat"
            />
          );
        },
      },
      {
        Header: "Entrepôt réception",
        // UI-only filter key (string) to avoid storing `{ $in: [...] }` objects as the input value
        filterName: "__warehouse",
        filterComponent: (props: any) => (
          <WarehouseSearchInput
            value={props.value}
            label=""
            onChange={(val, ids) => {
              props.onChange(val);
              if (ids?.length) {
                setFilter("warehouse", { $in: ids });
              } else {
                setFilter("warehouse", undefined);
              }
            }}
            orderModule
          />
        ),
        maxWidth: 145,
        minWidth: 145,
        Cell: ({ row: { original: currentReception } }: any) => {
          const company = getReceptionCompany(currentReception);
          return currentReception.id ? (
            <div
              onClick={() => handleClick(currentReception)}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text type={company?.isActive ? "text" : "disabled"}>
                {
                  (warehouses || []).find(
                    (e: any) => e._id === currentReception.warehouse
                  )?.name
                }
              </Text>
            </div>
          ) : (
            <WarehouseSelector
              disableClearable
              parentFilter={company?._id}
              parentFilterRequired
              onChange={(e, data) => {
                updateReception(currentReception, "warehouse", data?._id);
              }}
              value={
                getCompaniesOrParent(
                  warehouses,
                  currentReception.warehouse
                ).find((c) => c === company?._id) // verify warehouse belong to current company
                  ? warehouses?.find(
                      (e) => e._id === currentReception.warehouse
                    )
                  : null
              }
              error={errors.warehouse}
              hideError
              hideLabel
              variant="outlined"
            />
          );
        },
      },
      {
        Header: "Id Récep. entrepôt",
        filterName: "warehouseReference",
        minWidth: 110,
        maxWidth: 110,
        Cell: ({ row: { original: currentReception } }: any) => {
          const company = getReceptionCompany(currentReception);
          return currentReception.id &&
            !editedRowsId.includes(currentReception.id) ? (
            <div
              onClick={() => handleClick(currentReception)}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text type={company?.isActive ? "text" : "disabled"}>
                {currentReception.warehouseReference}
              </Text>
            </div>
          ) : (
            <TextField
              className={commonClasses.select}
              variant="outlined"
              defaultValue={currentReception.warehouseReference}
              // onBlur to prevent from been eject of editing at updateReception
              onBlur={(e) => {
                updateReception(
                  currentReception,
                  "warehouseReference",
                  e.target.value
                );

                if (
                  items?.find(
                    (r: any) => r.warehouseReference === e.target.value
                  ) !== undefined
                ) {
                  openWarningSnackbar(
                    `Attention, la référence ID de réception entrepôt "${e.target.value}" est déjà utilisée.
                    Vous pouvez quand même créer la réception.`,
                    5000
                  );
                }
              }}
            />
          );
        },
      },
      currentTab
        ? {
            Header: "Fournisseur / Entrepot d'origine",
            // UI-only filter key (string) to avoid storing `{ $in: [...] }` objects as the input value
            filterName: "__warehouse_origin",
            minWidth: 125,
            maxWidth: 125,
            filterComponent: (props: any) => (
              <CompanySearchInput
                value={props.value}
                label={null}
                onChange={(val, ids) => {
                  props.onChange(val);
                  if (!val) {
                    setFilter("warehouseOrigin", undefined);
                    return;
                  }
                  const value = ids.length ? ([...ids, val] as any) : [val];
                  setFilter("warehouseOrigin", { $in: value });
                }}
                orderModule
              />
            ),
            Cell: ({ row: { original: currentReception } }: any) => {
              if (currentReception.id) {
                const fullWarehouseOrigin =
                  (warehouses || []).find(
                    (c: any) => c._id === currentReception.warehouseOrigin
                  ) ?? null;
                return (
                  <div
                    onClick={() => handleClick(currentReception)}
                    role="button"
                    tabIndex={0}
                    aria-hidden="true"
                    className={jclasses.tableClickableDiv}
                  >
                    <Text type="text">{fullWarehouseOrigin?.name}</Text>
                  </div>
                );
              }
              const warehouseOrigin = (currentReception?.orders || []).find(
                (order) => order.warehouseOrigin
              )?.warehouseOrigin;
              if (warehouseOrigin && !isWarehouseSelected) {
                setIsWarehouseSelected(true);
                updateReception(
                  currentReception,
                  "warehouseOrigin",
                  warehouseOrigin
                );
              }
              return (
                <WarehouseSelect
                  disabled={!isWarehouseSelected}
                  label={null}
                  noPlaceHolder
                  warehouseOptions={warehouses}
                  onChange={(id) => {
                    updateReception(currentReception, "warehouseOrigin", id);
                  }}
                  error={errors.warehouse_origin as unknown as boolean}
                  value={warehouseOrigin}
                  onBlur={(e) => {
                    updateReception(
                      currentReception,
                      "warehouse_origin",
                      e.target.value
                    );
                  }}
                />
              );
            },
          }
        : {
            Header: "Fournisseur réception",
            // UI-only filter key (string) to avoid storing `{ $in: [...] }` objects as the input value
            filterName: "__provider",
            minWidth: 125,
            maxWidth: 125,
            filterComponent: (props: any) => (
              <CompanySearchInput
                value={props.value}
                label={null}
                onChange={(val, ids) => {
                  props.onChange(val);
                  if (!val) {
                    setFilter("provider", undefined);
                    return;
                  }
                  const value = ids.length ? ([...ids, val] as any) : [val];
                  setFilter("provider", { $in: value });
                }}
                orderModule
              />
            ),
            Cell: ({ row: { original: currentReception } }: any) => {
              // warning: provider can be an id or a name in DB
              // Check if we're working with Strapi v4 format or direct properties
              const providerValue = currentReception.provider;
              const companyValue = currentReception.company;

              const fullProvider =
                (companies || []).find(
                  (c: any) =>
                    c._id === providerValue || c.name === providerValue
                ) ?? null;

              return currentReception.id ? (
                <div
                  onClick={() => handleClick(currentReception)}
                  role="button"
                  tabIndex={0}
                  aria-hidden="true"
                  className={jclasses.tableClickableDiv}
                >
                  <Text type={fullProvider?.isActive ? "text" : "disabled"}>
                    {fullProvider?.name}
                  </Text>
                </div>
              ) : (
                <ProviderSelect
                  value={providerValue}
                  companyId={companyValue}
                  onChange={(providerId) => {
                    updateReception(currentReception, "provider", providerId);
                  }}
                  onBlur={(e) => {
                    updateReception(
                      currentReception,
                      "provider",
                      e.target.value
                    );
                  }}
                  error={errors.provider as unknown as boolean}
                />
              );
            },
          },
      {
        Header: "N° Bon livraison.",
        filterName: "blNumber",
        minWidth: 115,
        maxWidth: 115,
        Cell: ({ row: { original: currentReception } }: any) => {
          const company = getReceptionCompany(currentReception);
          return currentReception.id &&
            !editedRowsId.includes(currentReception.id) ? (
            <div
              onClick={() => handleClick(currentReception)}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text type={company?.isActive ? "text" : "disabled"}>
                {currentReception.blNumber}
              </Text>
            </div>
          ) : (
            <TextField
              className={commonClasses.select}
              variant="outlined"
              defaultValue={currentReception.blNumber}
              // onBlur to prevent from been eject of editing at updateReception
              onBlur={(e) => {
                updateReception(currentReception, "blNumber", e.target.value);
              }}
            />
          );
        },
      },
      {
        Header: "Statut",
        filterName: "status",
        filterComponent: (props: any) => (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
            }}
            disabled={isContributor()}
            value={props.value}
            options={Object.values(ReceptionStatus).map((val) => ({
              value: val,
              label: RECEPTION_STATUS_LABELS[val],
            }))}
          />
        ),
        minWidth: 80,
        maxWidth: 80,
        Cell: ({ row: { original: currentReception } }: any) => {
          const company = getReceptionCompany(currentReception);
          return currentReception.id &&
            !editedRowsId.includes(currentReception.id) ? (
            <div
              style={{
                marginBottom: 2,
                marginTop: 2,
                display: "flex",
                width: "100%",
              }}
              onClick={() => handleClick(currentReception)}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <JBadge
                disabled={!company?.isActive}
                variant={currentReception?.status}
                messages={STATUS_RECEPTION}
              />
            </div>
          ) : (
            <div
              style={{
                marginBottom: 2,
                marginTop: 2,
                display: "flex",
                width: "100%",
              }}
            >
              <JBadge
                variant={
                  currentReception?.reference ? currentReception?.status : "new"
                }
                messages={STATUS_RECEPTION}
              />
            </div>
          );
        },
      },
      {
        Header: "Statut Réf",
        filterName: "referencingStatus",
        filterComponent: (props: any) => (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
            }}
            value={props.value}
            options={Object.values(ReferencingStatus).map((val) => ({
              value: val,
              label: REFERENCING_STATUS_LABELS[val],
            }))}
          />
        ),
        minWidth: 95,
        maxWidth: 95,
        Cell: ({ row: { original: currentReception } }: any) => {
          // Safely get id from either attributes or directly
          const receptionId = currentReception?.id;

          // Safely get referencingStatus from either attributes or directly
          const referencingStatus = currentReception?.referencingStatus;

          return receptionId && !editedRowsId.includes(receptionId) ? (
            <div
              style={{
                marginBottom: 2,
                marginTop: 2,
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
              onClick={() => handleClick(currentReception)}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Tooltip title={REFERENCING_STATUS_LABELS[referencingStatus]}>
                <Chip
                  size="small"
                  className={
                    referencingStatus === ReferencingStatus.MISSING
                      ? classes.ReferencingStatusMissing
                      : referencingStatus === ReferencingStatus.TOTAL
                        ? classes.ReferencingStatusTotal
                        : ""
                  }
                />
              </Tooltip>
            </div>
          ) : (
            <div
              style={{
                marginBottom: 2,
                marginTop: 2,
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Chip
                size="small"
                className={
                  referencingStatus === ReferencingStatus.MISSING
                    ? classes.ReferencingStatusMissing
                    : referencingStatus === ReferencingStatus.TOTAL
                      ? classes.ReferencingStatusTotal
                      : ""
                }
              />
            </div>
          );
        },
      },
      ...(isAdmin && process.env.REACT_APP_SYNC_ENV === "prod"
        ? [
            {
              Header: "Synchronisation",
              filterName: "__isSynchronized",
              filterComponent: () => (
                <GenericSelect
                  className={jclasses.jTableLabelHeader}
                  onChange={(val) => {
                    setSynchronization_tfilter(val);
                  }}
                  value={synchronization_tfilter}
                  options={Object.values(
                    RECEPTION_SYNCHRONIZED_STATUS_FILTER
                  ).map((val) => ({
                    value: val,
                    label: val,
                  }))}
                />
              ),
              minWidth: 230,
              maxWidth: 230,
              Cell: ({ row: { original: currentReception } }: any) =>
                currentReception.id &&
                !editedRowsId.includes(currentReception.id) ? (
                  <div style={{ flex: 1 }}>
                    {getReceptionSynchronizedStatusButton(currentReception)}
                  </div>
                ) : null,
            },
          ]
        : []),
      ...(isAdmin()
        ? [
            // Actions are handled in the drawer (edit/delete), not in the table anymore.
          ]
        : []),
      {
        Header: null,
        accessor: "expander",
        minWidth: 60,
        maxWidth: 60,
        preventClickAction: true,
        Cell: ({ row, row: { original } }: any) => {
          // Access orders directly (Prisma format)
          const orders = original?.orders || [];

          return (
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              {orders?.length > 0 &&
              orders.find((order: any) => {
                // Check for suborders (Prisma format)
                const suborders = order?.suborders;
                return suborders?.length > 0;
              }) ? (
                <span
                  onClick={row.getToggleExpandedHandler()}
                  style={{
                    minWidth: 30,
                    padding: 3,
                    marginLeft: 4,
                    position: "relative",
                    top: 2,
                  }}
                >
                  {row.getIsExpanded() ? (
                    <ArrowUpSLineIcon />
                  ) : (
                    <ArrowDownSLineIcon />
                  )}
                </span>
              ) : null}
            </div>
          );
        },
      },
    ];
  }, [
    currentTab,
    isAdmin,
    getReceptionCompany,
    jclasses.tableClickableDiv,
    jclasses.jTableLabelHeader,
    handleClick,
    filterDateStart,
    filterDateEnd,
    onDateChange,
    onDatePickerClose,
    updateReception,
    editedRowsId,
    OrderSelectMemo,
    setFilter,
    companies,
    commonClasses.select,
    companiesOptions,
    warehouses,
    getCompaniesOrParent,
    errors.warehouse,
    errors.warehouse_origin,
    errors.provider,
    items,
    openWarningSnackbar,
    isWarehouseSelected,
    isContributor,
    classes.ReferencingStatusMissing,
    // classes.ReferencingStatusPartial, supprimer dans le BL-924
    classes.ReferencingStatusTotal,
    synchronization_tfilter,
    setSynchronization_tfilter,
    getReceptionSynchronizedStatusButton,
    companySidebarFilter,
  ]);

  // Implement row sub-component renderer
  const renderRowSubComponent = useCallback(
    ({ row }) => {
      let subItems = [];
      const tmpOrders = row?.original?.orders || [];

      for (const order of tmpOrders) {
        const suborders = order?.suborders || [];
        const tmp = subItems.concat(suborders);
        subItems = [...tmp];
      }

      const subColumns = [
        {
          Header: "Référence",
          id: "reference",
          Cell: ({
            row: {
              original: {
                reference,
                type,
                suborderProducts,
                dockyard,
                project,
              },
            },
          }: any) => {
            const fromOrder = tmpOrders.find((order) => {
              const suborders = order?.suborders || [];

              return suborders.some(
                (suborder) => suborder.reference === reference
              );
            });
            const renderedReference =
              fromOrder.suborders?.length === 1
                ? fromOrder.reference
                : reference;

            // Safely access nested properties
            const projectData = getProjectsFromItems.find(
              (p) => p._id === project
            );
            const dockyardData = getDockyardsFromItems.find(
              (d) => d._id === dockyard
            );

            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                <ArrowRightAlt
                  style={{ marginRight: 4, opacity: 0.6, fontSize: "1em" }}
                />
                <Text>
                  N° {renderedReference}, {suborderProducts?.length} produit
                  {suborderProducts?.length > 1 && "s"} (
                  {getSuborderTypeInfo(type, projectData, dockyardData)})
                </Text>
              </div>
            );
          },
        },
      ];

      return tmpOrders.length > 0 &&
        tmpOrders.find((order: any) => {
          const suborders = order?.suborders || [];
          return suborders?.length > 0;
        }) ? (
        <JTable
          data={subItems ?? []}
          columns={subColumns}
          isFetching={isFetching}
          pageCount={pageCount}
          filters={filters}
          setFilter={setFilter}
          setPageIndex={setPageIndex}
          pageIndex={pageIndex}
          isSubTable
        />
      ) : null;
    },
    [
      isFetching,
      pageCount,
      filters,
      setFilter,
      setPageIndex,
      pageIndex,
      getProjectsFromItems,
      getDockyardsFromItems,
      getSuborderTypeInfo,
    ]
  );

  return {
    receptions,
    isFetching,
    pageCount,
    pageIndex,
    filters,
    setFilter,
    setPageIndex,
    columns, // Assumed to be defined in the full component
    renderRowSubComponent, // Assumed to be defined in the full component
    open,
    toggleReceptionPopin,
    onAddReception,
    isSaving,
    editedReception,
    onSubmitReception,
    canDeleteEditedReception,
    onDeleteEditedReception,
    showOrderModal,
    setShowOrderModal,
    orderNewValue,
    setOrderNewValue,
    onSubmitOrder,
    getReceptionCompany,
    companies,
    companiesOptions,
    warehouses,
    updateReception,
    getCompaniesOrParent,
    classes,
    theme,
    jclasses,
    errors,
    editedRowsId,
    setEditedRowsId,
    removeReceptionItem,
    handleSaveReception,
    isProductDeletable,
    removeListItem,
    onOrderSelectValueChanged,
    getReceptionSynchronizedStatusButton,
    getReceptionSynchronizedStatus,
    isWarehouseSelected,
    setIsWarehouseSelected,
    isExporting,
    selectedRowsReception,
    setSelectedRowsReception,
    getParamsAsStrapiString,
    openErrorSnackbar,
    synchronization_tfilter,
    setSynchronization_tfilter,
    fetchItems, // Use adapter's refetch function
    dateStart_tfilter: filterDateStart,
    dateEnd_tfilter: filterDateEnd,
    setDateStart_tfilter,
    setDateEnd_tfilter,
    onDateChange,
    exportReceptions,
    isAdmin: isAdmin(),
    currentTab,
    itemsPerPage: pageSize,
    setItemsPerPage: setPageSize,
    edbData: getEdbData,
  };
}

export default useReceptionList;
